import React from "react";
import {
  findObjectUseCase,
  countObjectUseCase,
  deleteObjectUseCase,
  upsertUseCase,
} from "../../../usecases/object";
import BaseListPage from "../../../base/BaseListPage";
import NavBar from "../../../components/navbar";
import EmployeeMasterPagePresenter from "./EmployeeMasterPagePresenter";
import { Progress, InfiniteScroll } from "../../../nq-component";
import EmployeeTable from "./components/EmployeeTable";
import TableHeader from "./components/TableHeader";
import { formatBirthDate, formatDate, getEmployeeId } from "./utils/utils";

class EmployeeMasterPage extends BaseListPage {
  constructor(props) {
    super(props);
    this.state = {
      employees: [],
      users: [],
      progress: true,
      animationComplete: false,
      total: 0,
      count: 0,
      objects: [],
      selected: [],
    };
    this.presenter = new EmployeeMasterPagePresenter(
      this,
      findObjectUseCase(),
      countObjectUseCase(),
      deleteObjectUseCase(),
      upsertUseCase()
    );
    this.findObjectUseCase = findObjectUseCase();
  }

  componentDidMount() {
    this.presenter.componentDidMount();
    setTimeout(() => {
      this.fetchEmployeesData();
    }, 100);
  }

  getCollectionName() {
    return "employees";
  }

  getAcl() {
    const user = this.getCurrentUser();
    return {
      read: ["*", user.id],
      write: ["*", user.id],
    };
  }

  async fetchEmployeesData() {
    try {
      const employees = await this.findObjectUseCase.execute(
        "employees",
        {
          include: ["all"],
        },
        {},
        0,
        1000
      );

      const users = await this.findObjectUseCase.execute(
        "users",
        {},
        {},
        0,
        1000
      );

      this.setState(
        {
          employees,
          users,
          progress: false,
          objects: employees,
          count: employees.length,
          total: employees.length,
        },
        () => {
          setTimeout(() => {
            this.setState({ animationComplete: true });
          }, 50);
        }
      );
    } catch (error) {
      console.error("Error fetching data:", error);
      this.setState({
        progress: false,
        animationComplete: true,
        error: "Failed to fetch employee data",
      });
    }
  }

  downloadCSV = () => {
    const { employees, users } = this.state;

    const headers =
      "No.,Employee ID,First Name,Middle Name,Last Name,Sex,Telephone No.,Birth Month,Birth Day,Birth Year,Position,Employment Status,Date Start,SSS Number,Philhealth Number,Pagibig Number,TIN Number\n";

    const dataRows = employees
      .map((emp, index) => {
        const birthDate = formatBirthDate(emp.birthDate);
        const startDate = formatDate(emp.startDate);
        const employeeId = getEmployeeId(emp, users);

        const employeePosition =
          typeof emp.position === "string"
            ? emp.position.split(",").join("/")
            : "-";
        return [
          index + 1,
          employeeId,
          emp.lastName || "-",
          emp.firstName || "-",
          emp.middleName || "-",
          emp.sex || "-",
          emp.mobileNumber || "-",
          birthDate.month,
          birthDate.day,
          birthDate.year,
          employeePosition || "-",
          emp.status || "Active",
          startDate.month + "/" + startDate.day + "/" + startDate.year,
          emp.sss || "",
          emp.philHealth || "",
          emp.pagIbig || "",
          emp.tin || "",
        ].join(",");
      })
      .join("\n");

    const csvContent = headers + dataRows;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    const today = new Date().toISOString().slice(0, 10);
    link.setAttribute("download", `employee_master_list_${today}.csv`);
    link.click();
    URL.revokeObjectURL(url);
  };

  render() {
    const { progress, animationComplete, employees, error, users } = this.state;
    const schema = this.getSchema(this.getCollectionName());
    const user = this.getCurrentUser();

    if (error) {
      return (
        <>
          <NavBar user={user} />
          <div className="p-3 p-lg-5">
            <div className="bg-white p-4 shadow rounded-lg">
              <div className="alert alert-danger" role="alert">
                {error}. Please refresh the page or contact support.
              </div>
            </div>
          </div>
        </>
      );
    }

    if (!schema) {
      return (
        <>
          <NavBar user={user} />
          <div className="p-3 p-lg-5">
            <div className="bg-white p-4 shadow rounded-lg">
              <div className="alert alert-warning" role="alert">
                Schema not found. Please refresh the page or contact support.
              </div>
            </div>
          </div>
        </>
      );
    }
    console.log("employees", employees);
    return (
      <>
        <NavBar user={user} />

        {progress ? (
          <div className="d-flex justify-content-center align-items-center h-100">
            <Progress />
          </div>
        ) : (
          <div className="overflow-auto">
            <InfiniteScroll
              className="h-100"
              loadMore={this.loadMore ? this.loadMore.bind(this) : null}
              hasMore={false}
            >
              <div className="p-4">
                <div
                  className={`bg-white p-4 shadow rounded-lg ${
                    animationComplete ? "opacity-100" : "opacity-0"
                  }`}
                  style={{
                    transition: "all 0.5s ease-in-out",
                    transform: animationComplete
                      ? "translateY(0)"
                      : "translateY(20px)",
                  }}
                >
                  <TableHeader onDownload={this.downloadCSV.bind(this)} />

                  <div className="scroll-indicator mb-2">
                    <div className="scroll-text bg-light">
                      <i className="bi bi-arrow-left-right me-1"></i>
                      Scroll horizontally to view all columns
                    </div>
                  </div>

                  <EmployeeTable employees={employees} users={users} />
                </div>
              </div>
            </InfiniteScroll>
          </div>
        )}

        <style jsx>{`
          .employee-row {
            transition: all 0.2s ease;
          }
          .employee-row:hover {
            background-color: rgba(209, 130, 55, 0.05);
          }
          th {
            color: #555;
            font-weight: 600;
            font-size: 0.85rem;
            white-space: nowrap;
            text-align: center;
          }
          td {
            vertical-align: middle;
            font-size: 0.9rem;
            text-align: center;
          }
          .table-responsive {
            overflow-x: auto;
            margin-bottom: 1rem;
          }
          .table {
            border-collapse: collapse;
          }
          .table-bordered th,
          .table-bordered td {
            border: 1px solid #dee2e6;
          }
          /* Custom scrollbar styles */
          .custom-scrollbar::-webkit-scrollbar {
            height: 8px;
          }
          .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #d18237;
            border-radius: 4px;
          }
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #c07733;
          }
          /* Scroll indicator */
          .scroll-indicator {
            display: flex;
            justify-content: flex-end;
          }
          .scroll-text {
            font-size: 0.8rem;
            color: #666;
            display: flex;
            align-items: center;
            padding: 4px 8px;
            background-color: #f8f9fa;
            border-radius: 4px;
            animation: pulse 2s infinite;
          }
          @keyframes pulse {
            0% {
              opacity: 0.6;
            }
            50% {
              opacity: 1;
            }
            100% {
              opacity: 0.6;
            }
          }
        `}</style>
      </>
    );
  }
}

export default EmployeeMasterPage;
